import { useEffect, useState } from "react"
import { emptyItem, type ItemDraft } from "./custom-intake"
import { clearDraft, DRAFTS_CHANGED, readDraft, writeDraft } from "./drafts"
import { newRequestId } from "./payment-links"

/**
 * New custom orders being filled, several at once (owners, 16 Oct 2026: Ilda keeps two or more orders
 * open, goes to the Supplier finder, and adds what she finds to the order it belongs to without losing
 * anything). Each one is a draft on this device (lib/drafts.ts) under `custom-new:wb:<id>`, opened at
 * `/custom/new?w=<id>`, with its own request id, so saving it is still one order. Saving the order, or
 * «Start over», removes it.
 */

export type OrderDraft = { handle: string; shipping: string; note: string; items: ItemDraft[]; discount: string; amount: string; method: string; status: string; info: string }
export type OpenOrder = { id: string; saved_at: number; draft: Partial<OrderDraft>; label: string; items: number }

const PREFIX = "custom-new:wb:"
export const workbenchKey = (id: string) => `${PREFIX}${id}`
export const newWorkbenchId = () => Math.random().toString(36).slice(2, 10)

const filled = (item: ItemDraft) => !!(item.en.trim() || item.link.trim() || item.yuan.trim() || item.eur.trim())

/** What an open order is called in lists: its customer, else its first item, else "New order". */
export function orderLabel(draft: Partial<OrderDraft>, fallback: string): string {
  const handle = draft.handle?.replace(/^@/, "").trim()
  if (handle) return `@${handle}`
  const first = draft.items?.find(filled)
  return first ? (first.en.trim() || first.link.trim()).slice(0, 40) : fallback
}

export function listOpenOrders(fallback = "New order"): OpenOrder[] {
  const out: OpenOrder[] = []
  try {
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const full = window.localStorage.key(i)
      if (!full?.startsWith(`hq-draft:${PREFIX}`)) continue
      const id = full.slice(`hq-draft:${PREFIX}`.length)
      const stored = readDraft<Partial<OrderDraft>>(workbenchKey(id))
      if (!stored) continue
      out.push({ id, saved_at: stored.saved_at, draft: stored.value, label: orderLabel(stored.value, fallback), items: (stored.value.items ?? []).filter(filled).length })
    }
  } catch {
    // Storage blocked: no open orders to show.
  }
  return out.sort((a, b) => b.saved_at - a.saved_at)
}

/**
 * Puts an item into an open order (`id`), or into a new one (`id` null); answers the order's id. The
 * order's one empty line is used first, so a fresh order does not start with a blank item.
 */
export function addToOpenOrder(id: string | null, item: ItemDraft): string {
  const target = id ?? newWorkbenchId()
  const stored = id ? readDraft<Partial<OrderDraft>>(workbenchKey(id)) : null
  const items = stored?.value.items ?? []
  const next = items.length === 1 && !filled(items[0]) ? [item] : [...items, item]
  writeDraft(workbenchKey(target), { ...(stored?.value ?? {}), items: next }, { request_id: stored?.extra?.request_id ?? newRequestId() })
  return target
}

export const discardOpenOrder = (id: string) => clearDraft(workbenchKey(id))

/** The open orders, kept up to date when any of them changes (here or in another tab). */
export function useOpenOrders(fallback: string): OpenOrder[] {
  const [orders, setOrders] = useState(() => listOpenOrders(fallback))
  useEffect(() => {
    const refresh = () => setOrders(listOpenOrders(fallback))
    refresh()
    window.addEventListener(DRAFTS_CHANGED, refresh)
    window.addEventListener("storage", refresh)
    return () => {
      window.removeEventListener(DRAFTS_CHANGED, refresh)
      window.removeEventListener("storage", refresh)
    }
  }, [fallback])
  return orders
}

/** A product found at a supplier, as an order line: ¥ is the supplier's price plus shipping inside China. */
export function foundItem(found: { en?: string | null; zh?: string | null; link: string; cost_yuan?: number | null; pick_id?: string | null }): ItemDraft {
  return {
    ...emptyItem(),
    en: (found.en ?? "").slice(0, 400),
    zh: found.zh ?? "",
    link: found.link,
    yuan: found.cost_yuan != null ? String(found.cost_yuan) : "",
    ...(found.pick_id ? { pick_id: found.pick_id } : {}),
  }
}
